import { AQIStatus, AQIStatusColor } from "../types";

export function aqiStatus(aqi: number) {
  if (aqi <= 50) {
    return {
      status: AQIStatus.GOOD,
      statusColor: AQIStatusColor.GOOD,
    };
  } else if (aqi <= 100) {
    return {
      status: AQIStatus.MODERATE,
      statusColor: AQIStatusColor.MODERATE,
    };
  } else if (aqi <= 150) {
    return {
      status: AQIStatus.UNHEALTHY_FOR_SENSITIVE_GROUPS,
      statusColor: AQIStatusColor.UNHEALTHY_FOR_SENSITIVE_GROUPS,
    };
  } else if (aqi <= 200) {
    return {
      status: AQIStatus.UNHEALTHY,
      statusColor: AQIStatusColor.UNHEALTHY,
    };
  } else if (aqi <= 300) {
    return {
      status: AQIStatus.VERY_UNHEALTHY,
      statusColor: AQIStatusColor.VERY_UNHEALTHY,
    };
  } else {
    return {
      status: AQIStatus.HAZARDOUS,
      statusColor: AQIStatusColor.HAZARDOUS,
    };
  }
}

export function getAQIPercentage(aqi?: number | null) {
  if (aqi) {
    return (aqi / 301) * 100 > 100 ? 100 : (aqi / 301) * 100;
  }
  return 0;
}

export function getPM10Percentage(pm10: number) {
  return (pm10 / 604) * 100 > 100 ? 100 : (pm10 / 604) * 100;
}

export function getPM25Percentage(pm25: number) {
  return (pm25 / 500.4) * 100 > 100 ? 100 : (pm25 / 500.4) * 100;
}

function mgToPPM(stat: number) {
  return stat * 0.001;
}

export function getOzonePercentage(ozone: number) {
  const ozonePPM = mgToPPM(ozone);
  return (ozonePPM / 0.2) * 100 > 100 ? 100 : (ozonePPM / 0.2) * 100;
}

export const checkAQIMeasurements = (returnVal: any, value?: number | null) =>
  value && value === -999 ? undefined : returnVal;

enum AQIQualitativeName {
  GOOD = "Good",
  FAIR = "Fair",
  MODERATE = "Moderate",
  POOR = "Poor",
  VERY_POOR = "Very Poor",

  UNKNOWN = "Unknown",
}
export class AQIQualitativeNamesService {
  public qualitativeName(
    metric: number,
    type: "pm2_5" | "pm10" | "o3" | "aqi" | "no2",
  ): AQIQualitativeName {
    switch (type) {
      case "pm2_5":
        return this.pm25QualitativeName(metric);
      case "pm10":
        return this.pm10QualitativeName(metric);
      case "o3":
        return this.o3QualitativeName(metric);
      case "no2":
        return this.no2QualitativeName(metric);
      case "aqi":
        return this.getQualitativeName(metric);
    }
  }

  /**
   * Converts __PM2.5__ concentrations to Qualitative Names
   */
  pm25QualitativeName(metric: number): AQIQualitativeName {
    if (metric === -999) {
      return this.getQualitativeName(metric);
    }
    const index = this.calculatePM25Index(metric);
    return this.getQualitativeName(index);
  }

  /**
   * Converts __PM10__ concentrations into Qualitative Names
   */
  pm10QualitativeName(metric: number): AQIQualitativeName {
    if (metric === -999) {
      return this.getQualitativeName(metric);
    }
    const index = this.calculatePM10Index(metric);
    return this.getQualitativeName(index);
  }

  /**
   * Converts __O3__ concentrations into Qualitative Name
   */
  o3QualitativeName(metric: number): AQIQualitativeName {
    if (metric === -999) {
      return this.getQualitativeName(metric);
    }
    const index = this.calculateO3Index(metric);
    return this.getQualitativeName(index);
  }

  /**
   * Converts __NO3__ concentrations into Qualitative Name
   */

  no2QualitativeName(metric: number): AQIQualitativeName {
    if (metric === -999) {
      return this.getQualitativeName(metric);
    }
    const index = this.calculateNO2Index(metric);
    return this.getQualitativeName(index);
  }

  private calculatePM25Index(metric: number): number {
    if (metric < 15) return 1;
    if (metric < 30) return 2;
    if (metric < 55) return 3;
    if (metric < 110) return 4;
    if (metric > 110) return 5;
    // Default index of 0 always indicates an unknown or null value
    return 0;
  }

  private calculatePM10Index(metric: number): number {
    if (metric < 25) return 1;
    if (metric < 50) return 2;
    if (metric < 90) return 3;
    if (metric < 180) return 4;
    if (metric < 240) return 5;
    if (metric > 240) return 6;
    // Default index of 0 always indicates an unknown or null value
    return 0;
  }

  private calculateO3Index(metric: number): number {
    if (metric < 60) return 1;
    if (metric < 120) return 2;
    if (metric < 180) return 3;
    if (metric < 240) return 4;
    if (metric > 240) return 5;
    // Default index of 0 always indicates an unknown or null value
    return 0;
  }

  private calculateNO2Index(metric: number): number {
    if (metric < 50) return 1;
    if (metric < 100) return 2;
    if (metric < 200) return 3;
    if (metric < 400) return 4;
    if (metric > 400) return 5;
    // Default index of 0 always indicates an unknown or null value
    return 0;
  }

  /**
   * Qualitative Names for describing Air Quality index levels
   * |Qualitative name|Index|NO2|PM10|O3|PM25|
|:---:|:---:|:---:|:---:|:---:|:---:|
|Good|1|0-50|0-25|0-60|0-15|
|Fair|2|50-100|25-50|60-120|15-30|
|Moderate|3|100-200|50-90|120-180|30-55|
|Poor|4|200-400|90-180|180-240|55-110|
|Very Poor|5|>400|>180|>240|>110|
|Unknown|0|undefined|undefined|undefined|undefined|
   * > _All concentration values are in  μg/m3_
   */
  getQualitativeName(index: number): AQIQualitativeName {
    switch (index) {
      case 1:
        return AQIQualitativeName.GOOD;
      case 2:
        return AQIQualitativeName.FAIR;
      case 3:
        return AQIQualitativeName.MODERATE;
      case 4:
        return AQIQualitativeName.POOR;
      case 5:
        return AQIQualitativeName.VERY_POOR;
      default:
        return AQIQualitativeName.UNKNOWN;
    }
  }
}
